import type { LineItem } from '~~/types/commercetools';
import { orderGetters, cartGetters } from '~/utils/ct';

import type { LineItemExtended, LineItemWithGuaranteeInside } from '~~/types/product/LineItemExtended';
import { STANDARD_PRODUCT } from '~~/constants/productType';
import {
  getTotalGrossPrice,
} from '~~/helpers/cart/getTotalPricesModule';
import { getTotalGrossPriceBeforeDiscount } from '~~/helpers/cart/getTotalPrices';
import { getRawAttribute } from '~~/helpers/product/getRawAttributeAsArray';
import { PRODUCT_ATTRIBUTES } from '~~/constants/products';
import { DEFAULT_VOLUME_DISCOUNT_GROUP } from '~~/constants/volumeDiscounts';
import { PARENT_SKU } from '~~/constants/extraGuarantee';

export default function () {

  const { cart, isExtraGuaranteeOnlyCart } = useCartExtended();
  const {
    findProductStockBySku,
  } = useStock('cart');
  const { isGuaranteeActionBeingClicked, setIsGuaranteeActionBeingClicked } =
   useIsGuaranteeActionBeingClickedStore();

  const {
    order,
  } = useUserOrderExtended();

  const { load: loadVolumeDiscount, volumeDiscountConfigurations } = useVolumeDiscountsStore();

  const { isThankYouPage } = useIsPage();

  const { extraGuaranteeParentSku, shouldAddExtraGuarantee } = useExtraGuarantee();

  const orderProducts = computed<LineItemExtended[]>(() => {

    const orderItems = order.value ? orderGetters.getItems(order.value) : [];

    return orderItems.map((product) => {

      const totalGrossPrice = getTotalGrossPrice(product);

      return {
        ...product,
        image: cartGetters.getItemImage(product) || product?.variant?.images[0]?.url,
        title: orderGetters.getItemName(product),
        qty: orderGetters.getItemQty(product),
        priceBeforeDiscounts: getTotalGrossPriceBeforeDiscount(product),
        unitPrice: totalGrossPrice !== null ? totalGrossPrice / orderGetters.getItemQty(product) : null,
      };

    });
  
  });

  const getDiscountGroup = (product: LineItem) => {

    return getRawAttribute(product.variant, PRODUCT_ATTRIBUTES.VOLUME_DISCOUNT_GROUP) ||
    DEFAULT_VOLUME_DISCOUNT_GROUP;
  
  };
  const cartProducts = computed<LineItemExtended[]>(() => {

    const cartItems = cart.value ? cartGetters.getItems(cart.value) : [];

    return cartItems.map((cartItem) => {

      const volumeDiscountGroup = getDiscountGroup(cartItem);
      const totalGrossPrice = getTotalGrossPrice(cartItem);

      return {
        ...cartItem,
        image: cartGetters.getItemImage(cartItem) || cartItem?.variant?.images[0]?.url,
        title: cartGetters.getItemName(cartItem),
        unitPrice: totalGrossPrice !== null ? totalGrossPrice / cartGetters.getItemQty(cartItem) : null,
        qty: cartGetters.getItemQty(cartItem),
        priceBeforeDiscounts: getTotalGrossPriceBeforeDiscount(cartItem),
        stock: cartItem?.variant?.sku ? findProductStockBySku(cartItem.variant?.sku) : 0,
        volumeDiscountsForGroup: volumeDiscountConfigurations.value[volumeDiscountGroup],
      };

    });
  
  });

  const lineItems = computed<LineItemExtended[]>(() => {

    return (order.value && isThankYouPage.value) ? orderProducts.value : cartProducts.value;
  
  });

  const lineItemsWithGuaranteeCombined = computed<LineItemWithGuaranteeInside[]>(() => {

    const products: LineItemWithGuaranteeInside[] = lineItems.value;

    const standardProductsBySku = new Map<string, LineItemWithGuaranteeInside>();
    const guaranteeByParentSku = new Map<string, LineItemWithGuaranteeInside>();

    for (const product of products) {

      const sku = product.variant?.sku;

      if (!sku) {

        continue;

      }

      if (product.productType?.key === STANDARD_PRODUCT) {

        standardProductsBySku.set(sku, product);
      
      } else {

        const parentSku = extraGuaranteeParentSku(product);

        if (parentSku) {

          guaranteeByParentSku.set(parentSku, product);
        
        }
      
      }
    
    }

    const result: LineItemWithGuaranteeInside[] = [];
    for (const [sku, product] of standardProductsBySku.entries()) {

      const guarantee = guaranteeByParentSku.get(sku);

      if (guarantee) {

        result.push({ ...product, extraGuarantee: guarantee });
      
      } else {

        const isEligibleForGuarantee = shouldAddExtraGuarantee(product);

        if (isEligibleForGuarantee) {

          result.push({ ...product, isEligibleForGuarantee: true });
        
        } else {

          result.push(product);
        
        }
      
      }
    
    }

    if (result.length === 0 && isExtraGuaranteeOnlyCart.value) {

      for (const guaranteeProduct of guaranteeByParentSku.values()) {

        guaranteeProduct.parentSku = guaranteeProduct.custom?.customFieldsRaw?.find(
          (field) => field.name === PARENT_SKU)?.value;
        result.push({ ...guaranteeProduct, isEligibleForGuarantee: false });
      
      }
    
    }

    return result;
  
  });

  const loadVolumeDiscounts = () => {

    lineItems.value.forEach(async (product) => {

      const volumeDiscountGroup = getDiscountGroup(product);
      await loadVolumeDiscount(volumeDiscountGroup);
    
    });
  
  };

  return {
    lineItems,
    lineItemsWithGuaranteeCombined,
    loadVolumeDiscounts,
    isGuaranteeActionBeingClicked,
    setIsGuaranteeActionBeingClicked,
  };

}
